import React, { useState, useEffect } from 'react';

type Props = {
  src?: string | null;
  alt?: string;
  className?: string;
  fallbackSrc?: string;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  /** Fires once when the primary `src` fails (e.g. expired signed URL) before switching to fallback. */
  onPrimaryLoadFailed?: () => void;
};

const DEFAULT_PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#F3F4F6"/>
      <stop offset="1" stop-color="#E5E7EB"/>
    </linearGradient>
  </defs>
  <rect width="800" height="600" fill="url(#g)"/>
  <rect x="120" y="110" width="560" height="380" rx="28" fill="#FFFFFF" opacity="0.65"/>
  <path d="M320 290l70-70 110 120 70-70 110 120H280z" fill="#CBD5E1"/>
  <circle cx="355" cy="245" r="26" fill="#CBD5E1"/>
  <text x="400" y="520" text-anchor="middle" font-size="28" fill="#94A3B8" font-family="Arial">Image</text>
</svg>`
  );

export const ImageWithFallback: React.FC<Props> = ({
  src,
  alt = '',
  className,
  fallbackSrc,
  onLoad,
  onPrimaryLoadFailed,
}) => {
  const fallback = fallbackSrc ?? DEFAULT_PLACEHOLDER;
  const initial = src?.trim() ? src.trim() : fallback;
  const [currentSrc, setCurrentSrc] = useState(initial);

  useEffect(() => {
    setCurrentSrc(src?.trim() ? src.trim() : fallback);
  }, [src, fallback]);

  return (
    <img
      src={currentSrc}
      alt={alt}
      className={className}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onLoad={onLoad}
      onError={() => {
        if (currentSrc !== fallback) {
          const primary = src?.trim();
          if (primary && /^https?:\/\//i.test(primary) && onPrimaryLoadFailed) {
            onPrimaryLoadFailed();
          }
          setCurrentSrc(fallback);
        }
      }}
    />
  );
};
